import './record-search-screen.scss'

import _ from 'lodash'
import { delay, first, map, retryWhen, switchMap, take, tap } from 'rxjs/operators'
import { defineComponent } from 'vue'
import { faCaretUp } from '@fortawesome/free-solid-svg-icons'

import { AuditCampaign, AuditCampaignFeatured, Item } from '@skinsoft/types'
import { AuditRoutes } from '@skinsoft/const'
import { auditCampaignSelectors } from '@skinsoft/selectors'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { SearchHeader } from '@skinsoft/search'
import { toCampaignFeatured$ } from '@skinsoft/entity-ui'
import { AddItemModal, CollaboratorView, CollapsibleHeader } from '@skinsoft/ui'
import { TOGGLE_MENU_TOPIC } from '@skinsoft/bus/src/busTopics'
import { RecordFeaturedList } from '../record-featured-list'
import { OPEN_ADD_ITEM_MODAL, PUSH_SYNCHRO_PERFORMED } from '../../../../busTopics'

declare const config
const RECORD_SORTING_ENTITY_FIELDS: any[] = config.recordSortingEntityFields

export const RecordSearchScreen = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sAddItemModal: AddItemModal,
        sCollaboratorView: CollaboratorView,
        sCollapsibleHeader: CollapsibleHeader,
        sRecordFeaturedList: RecordFeaturedList,
        sSearchHeader: SearchHeader,
    },
    props: ['titleI18NKey'],
    template: require('./record-search-screen.html'),
    data() {
        return {
            campaignId: "",
            campaign: null as any as AuditCampaign,
            campaignFeatured: null as any as AuditCampaignFeatured,
            nbRecords: "",
            targetRouteName: "/record",
            linkedRecordsIds: [] as string[],
            ready: false,
            // Icons
            arrowIcon: faCaretUp,
        }
    },
    computed: {
        itemsIdsToExclude(): string[] {
            return auditCampaignSelectors.linkableItems(this.campaign)
                        .filter(linkableItem => linkableItem.linkableItem?.linkableItem)
                        .map(linkableItem => linkableItem.linkableItem!.linkableItem) as string[]
        },
    },
    methods: {
        subscribe() {
            this.campaignId = this.$route.params.campaignId as string
            const locale = this.getAppLocale()

            if (this.campaignId) {
                this.subscribeTo(this.$auditCampaignDataStore.fetchOne(this.campaignId)
                    .pipe(
                        first(),// To avoid double emissions due to cache merging
                        switchMap(
                            campaign => {
                                this.campaign = campaign;
                                this.linkedRecordsIds = auditCampaignSelectors.auditRecords(campaign);
                                return toCampaignFeatured$(campaign, locale);
                            }
                        )),
                    campaignFeatured => {
                        this.campaignFeatured = campaignFeatured;
                        this.ready = true;
                    }
                    ,
                    'campaign-subscription')
            }

            this.$on('nb-fetched-reports-topic', (message) => {
                this.nbRecords = this.$t('listReportScreen.nbResults', { nbResults: message.nbRecords }) as string;
            })
            this.subscribeToBus(OPEN_ADD_ITEM_MODAL, this.openAddItemModal, 'on-open-add-item-modal')
            this.subscribeToBus(PUSH_SYNCHRO_PERFORMED, this.initialize, 'on-push-synchro-performed')
        },

        unsubscribe() {
            this.unsubscribeFrom('campaign-subscription')
            this.unsubscribeFrom('on-open-add-item-modal')
            this.unsubscribeFrom('on-push-synchro-performed')
        },

        openAddItemModal() {
            if (this.$refs.addItemModal) {
                (this.$refs.addItemModal as InstanceType<typeof AddItemModal>).open()
            }
        },

        onAddedItemSelected(item: Item) {
            const oldRecordslist = _.cloneDeep(auditCampaignSelectors.auditRecords(this.campaign));

            const getNewRecord = (campaign: AuditCampaign): string => {
                const newRecordslist = auditCampaignSelectors.auditRecords(campaign);

                // testing last record first id for performance purposes
                let recordId = newRecordslist[newRecordslist.length - 1];
                if (oldRecordslist.includes(recordId)) {
                    // fetching right id by difference between old and new array (if needed)
                    const recordsDiff = newRecordslist.filter(r => !oldRecordslist.includes(r))
                    if (recordsDiff && recordsDiff.length > 0) {
                        recordId = recordsDiff[0]
                        return recordId
                    }
                    return '' // no new record
                } else {
                    return recordId
                }
            }

            this.$auditCampaignDataStore.addItem(
                this.campaign,
                item.id,
                item.type ?? item.entityType
            ).then(
                response => {
                    this.unsubscribeFrom('campaign-subscription')
                    const newRecordId = getNewRecord(response)
                    if(newRecordId) {
                        this.openEditRecordScreen(newRecordId)
                    } else {
                        // the record is not created yet, wait and retry
                        this.$auditCampaignDataStore.fetchOne(this.campaign.id).pipe(
                            map(campaign => {
                                const newRecordId = getNewRecord(campaign)
                                if(newRecordId) {
                                    return newRecordId
                                }
                                throw new Error("Record not ready yet")
                            }),
                            retryWhen(err =>
                                err.pipe(
                                    delay(1000),
                                    take(20),
                                )
                            ),
                        ).subscribe(recordId => {
                            this.openEditRecordScreen(recordId)
                        })
                    }
                }
            )
        },

        openEditRecordScreen(recordId: string) {
            (this.$refs.addItemModal as InstanceType<typeof AddItemModal>).close()
            this.emitOnBus(TOGGLE_MENU_TOPIC);
            this.$router.push({ name: AuditRoutes.RECORD, params: { campaignId: this.$route.params.campaignId, recordId } })
        },

        initialize() {
            this.ready = false;
            this.unsubscribe();
            this.subscribe();
        },
    },
    watch: {
        route: {
            handler() {
                this.initialize();
            },
            immediate: true,
            deep: true,
        },
    },
})
